/**
 * Detecting a failed lazy-route import, kept dependency-free so it can be
 * tested without pulling in React or the Supabase client.
 *
 * Routes are code-split, so a route's JS is fetched on demand. Around a deploy
 * that fetch can fail and the route dies.
 *
 * This does NOT drive any recovery — recovery lives in exactly one place, the
 * `vite:preloadError` listener in main.tsx, which refetches the failing URL with
 * `cache: 'reload'` before reloading. This is used for two smaller things:
 * RootErrorBoundary tells the member "the app updated while this page was open"
 * rather than something generic, and the logged error is tagged `chunk: true`
 * so the two failure families can be told apart in the activity log.
 *
 * Match broadly — but not so broadly that ordinary application bugs are
 * mislabelled as deploy churn, which would send us hunting the wrong cause
 * exactly like the incidents this logging exists to prevent. The guard test
 * pins both sides.
 */

/**
 * Engine wordings for a failed module fetch. None of these are a stable API —
 * they are browser error strings and they do change, which is why the guard
 * test lists real observed examples rather than paraphrases.
 *
 * The MIME-type entries matter on Cloudflare specifically: a request for a
 * chunk that no longer exists is answered with the SPA's index.html, so the
 * browser complains about a bad module MIME type rather than reporting a 404.
 */
const CHUNK_PATTERNS: RegExp[] = [
  /failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /chunkloaderror/i, // webpack-style, and some bundler shims
  /loading chunk \S+ failed/i,
  /is not a valid javascript mime type/i, // index.html served for a dead chunk
  /expected a javascript(-or-wasm)? module script/i, // Chrome's MIME wording
];

export function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error
    ? `${error.name} ${error.message}`
    : String(error ?? '');
  return CHUNK_PATTERNS.some((re) => re.test(text));
}
